import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { getPagePath } from "../pageRegistry.ts";
import { useToast } from "../../hooks/useToast.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { useAuth } from "../../auth/authContext.tsx";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import {
  getBasePlanograms,
  getPlanogram,
  savePlanogram,
  pushPlanogram,
  getPlanogramPushHistory,
  type Planogram,
  type PlanogramSlot,
  type PlanogramPush,
} from "../../api/planogram.ts";
import type { KioskCacheItem, ProductCacheItem } from "../../types/session.ts";
import { Button } from "../../components/ui/button.tsx";
import { Input } from "../../components/ui/input.tsx";
import { Checkbox } from "../../components/ui/checkbox.tsx";
import { Badge } from "../../components/ui/badge.tsx";
import { EmptyState } from "../../components/ui/emptyState.tsx";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../components/ui/modal.tsx";
import formStyles from "../../styles/formPage.module.scss";
import styles from "./planogramPage.module.scss";

/* ── Slot editing modal ────────────────────────────────── */

function SlotEditModal({
  open,
  slot,
  isNew,
  products,
  onSave,
  onClose,
}: {
  open: boolean;
  slot: PlanogramSlot | null;
  isNew: boolean;
  products: ProductCacheItem[];
  onSave: (data: Partial<PlanogramSlot>) => void;
  onClose: () => void;
}) {
  const [productSearch, setProductSearch] = useState("");
  const [productID, setProductID] = useState(slot?.ProductID ?? 0);
  const [maxCount, setMaxCount] = useState(String(slot?.MaxProductCount ?? 0));
  const [rentable, setRentable] = useState(slot?.Rentable ?? false);
  const [disabled, setDisabled] = useState(slot?.Disabled ?? false);

  // Reset state whenever the modal is re-opened for a different slot.
  useEffect(() => {
    if (!open) return;
    setProductSearch("");
    setProductID(slot?.ProductID ?? 0);
    setMaxCount(String(slot?.MaxProductCount ?? 0));
    setRentable(slot?.Rentable ?? false);
    setDisabled(slot?.Disabled ?? false);
  }, [open, slot]);

  const filtered = useMemo(() => {
    if (!productSearch) return products.slice(0, 50);
    const q = productSearch.toLowerCase();
    return products.filter((p) => (p.Name ?? "").toLowerCase().includes(q)).slice(0, 50);
  }, [products, productSearch]);

  const selectedProduct = products.find((p) => p.ProductID === productID);
  const maxCountNum = Number(maxCount);
  const maxCountValid = maxCount !== "" && Number.isFinite(maxCountNum) && maxCountNum >= 0;

  const handleSave = () => {
    if (!maxCountValid) return;
    onSave({
      ProductID: productID,
      MaxProductCount: maxCountNum,
      Rentable: rentable,
      Disabled: disabled,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title={isNew ? "Create slot" : "Edit slot"} onClose={onClose} />
      <ModalBody>
        <div className={styles.modalForm}>
          <label className={styles.modalLabel}>
            Product
            <Input
              type="search"
              placeholder="Search products…"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              aria-describedby="slot-selected-product"
            />
          </label>

          <div id="slot-selected-product" className={styles.modalSelectedHint}>
            {selectedProduct
              ? `Selected: ${selectedProduct.Name}`
              : "No product selected"}
          </div>

          <div className={styles.listScrollSm}>
            <div className={styles.listScrollInner}>
              <ul
                className={styles.list}
                role="listbox"
                aria-label="Products"
              >
                <li
                  role="option"
                  aria-selected={productID === 0}
                  className={productID === 0 ? styles.listItemSelected : styles.listItem}
                  onClick={() => setProductID(0)}
                >
                  <span className={styles.listItemName}>(None)</span>
                </li>
                {filtered.map((p) => {
                  const isSel = productID === p.ProductID;
                  return (
                    <li
                      key={p.ProductID}
                      role="option"
                      aria-selected={isSel}
                      className={isSel ? styles.listItemSelected : styles.listItem}
                      onClick={() => setProductID(p.ProductID)}
                    >
                      <span className={styles.listItemName}>{p.Name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <label className={styles.modalLabel}>
            Max product count
            <Input
              type="number"
              min={0}
              value={maxCount}
              onChange={(e) => setMaxCount(e.target.value)}
              error={!maxCountValid}
              aria-invalid={!maxCountValid}
            />
          </label>

          <div className={styles.modalCheckRow}>
            <Checkbox
              label="Rentable"
              checked={rentable}
              onChange={(e) => setRentable(e.target.checked)}
            />
            <Checkbox
              label="Disabled"
              checked={disabled}
              onChange={(e) => setDisabled(e.target.checked)}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!maxCountValid}>
          {isNew ? "Create slot" : "Save slot"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/* ── Push planogram modal ──────────────────────────────── */

export interface PushPlanogramDialogProps {
  open: boolean;
  planogramId: number;
  kiosks: KioskCacheItem[];
  onClose: () => void;
}

export function PushPlanogramDialog({
  open,
  planogramId,
  kiosks,
  onClose,
}: PushPlanogramDialogProps) {
  const { toast } = useToast();
  const [kioskSearch, setKioskSearch] = useState("");
  const [selectedKioskID, setSelectedKioskID] = useState(0);
  const [history, setHistory] = useState<PlanogramPush[]>([]);
  const [pushing, setPushing] = useState(false);

  const loadHistory = useCallback(() => {
    getPlanogramPushHistory(planogramId).then((r) => {
      if (!r.bError && r.oData) setHistory(r.oData);
    });
  }, [planogramId]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  const filteredKiosks = useMemo(() => {
    if (!kioskSearch) return kiosks.slice(0, 50);
    const q = kioskSearch.toLowerCase();
    return kiosks
      .filter(
        (k) =>
          (k.StoreName ?? "").toLowerCase().includes(q) ||
          String(k.KioskID).includes(q),
      )
      .slice(0, 50);
  }, [kiosks, kioskSearch]);

  const handlePush = async () => {
    if (!selectedKioskID) {
      toast("Select a kiosk to push to.", "warning");
      return;
    }
    setPushing(true);
    try {
      const r = await pushPlanogram(planogramId, selectedKioskID);
      if (r.bError) {
        toast(r.sMessage || "Push failed", "error");
      } else {
        toast("Planogram pushed successfully.", "success");
        if (r.oData) setHistory(r.oData);
      }
    } catch {
      toast("Push failed", "error");
    } finally {
      setPushing(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} wide>
      <ModalHeader title="Push planogram" onClose={onClose} />
      <ModalBody>
        <div className={styles.modalForm}>
          <label className={styles.modalLabel}>
            Kiosk
            <Input
              type="search"
              placeholder="Search by kiosk ID or store name…"
              value={kioskSearch}
              onChange={(e) => setKioskSearch(e.target.value)}
            />
          </label>

          <div className={styles.listScrollSm}>
            <div className={styles.listScrollInner}>
              {filteredKiosks.length === 0 ? (
                <div className={styles.listEmpty}>No kiosks match.</div>
              ) : (
                <ul className={styles.list} role="listbox" aria-label="Target kiosks">
                  {filteredKiosks.map((k) => {
                    const isSel = selectedKioskID === k.KioskID;
                    return (
                      <li
                        key={k.KioskID}
                        role="option"
                        aria-selected={isSel}
                        className={isSel ? styles.listItemSelected : styles.listItem}
                        onClick={() => setSelectedKioskID(k.KioskID)}
                      >
                        <span className={styles.listItemName}>{k.StoreName}</span>
                        <span className={styles.listItemMeta}>Kiosk {k.KioskID}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className={styles.panelHeader}>
            <h3 className={styles.panelTitle}>Push history</h3>
            <span className={styles.panelCount}>{history.length} entries</span>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.slotTable} aria-label="Push history">
              <thead>
                <tr>
                  <th scope="col">Kiosk</th>
                  <th scope="col">Sync status</th>
                  <th scope="col">Pushed by</th>
                  <th scope="col">Pushed</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center" }}>
                      No push history
                    </td>
                  </tr>
                ) : (
                  history.map((h) => (
                    <tr key={h.ID}>
                      <td>{h.KioskID}</td>
                      <td>
                        <Badge variant={h.Synced ? "success" : "warning"}>
                          {h.Synced ? "Synced" : "Pending"}
                        </Badge>
                      </td>
                      <td>{h.PushedBy}</td>
                      <td>{h.Pushed}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={loadHistory}>
          Refresh
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handlePush}
          loading={pushing}
          disabled={!selectedKioskID}
        >
          Push planogram
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/* ── Main edit planogram dialog ────────────────────────── */

export interface EditPlanogramDialogProps {
  open: boolean;
  planogramId: number;
  onClose: () => void;
  onSaved?: () => void;
  /** New mode only (`planogramId` < 0): a planogram the caller already assembled to
   *  start from instead of picking a source — Issa's `createPlanogram` action hands
   *  over the kiosk's live layout with its suggested slots applied. Copied on mount;
   *  the operator can still load another source over it. `ID`/`KioskID` are forced
   *  to -1 on save as for any new planogram. */
  initialPlanogram?: Planogram;
  /** New mode only: pre-filled, still editable, planogram name. */
  initialName?: string;
  /** Names of the slots the caller changed in `initialPlanogram`. Those rows are
   *  marked in the slot table so the operator sees the suggestion against the
   *  kiosk's current layout. Matched case-insensitively. */
  highlightSlotNames?: readonly string[];
}

export function EditPlanogramDialog({
  open,
  planogramId,
  onClose,
  onSaved,
  initialPlanogram,
  initialName,
  highlightSlotNames,
}: EditPlanogramDialogProps) {
  const { toast } = useToast();
  const { confirm } = useAlertDialog();
  const { cache } = useAuth();

  const paramId = planogramId;
  const isNew = paramId < 0;

  const [allPlanograms, setAllPlanograms] = useState<Planogram[]>([]);
  // A caller-supplied starting layout only applies to a new planogram — editing an
  // existing one always shows the stored slots.
  const [current, setCurrent] = useState<Planogram | null>(() =>
    isNew && initialPlanogram
      ? { ...initialPlanogram, ID: -1, KioskID: -1, ConfigDataSerialized: [...initialPlanogram.ConfigDataSerialized] }
      : null,
  );
  const [name, setName] = useState(isNew ? (initialName ?? "") : "");
  const highlightedSlots = useMemo(
    () => new Set((highlightSlotNames ?? []).map((n) => n.trim().toLowerCase())),
    [highlightSlotNames],
  );
  const isHighlighted = (slot: PlanogramSlot) =>
    highlightedSlots.size > 0 && highlightedSlots.has((slot.Name ?? "").trim().toLowerCase());
  const [deleted, setDeleted] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal state
  const [editingSlot, setEditingSlot] = useState<PlanogramSlot | null | "new">(null);
  const [pushOpen, setPushOpen] = useState(false);

  const statusRef = useRef<HTMLDivElement>(null);

  const kiosks = useMemo(
    () => (cache?.CommonData?.Kiosk ?? []) as KioskCacheItem[],
    [cache],
  );
  const products = useMemo(
    () => (cache?.CommonData?.Product ?? []) as unknown as ProductCacheItem[],
    [cache],
  );
  const productMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of products) m.set(p.ProductID, p.Name);
    return m;
  }, [products]);

  // Load all planograms (for left panel / load-from source)
  useEffect(() => {
    getBasePlanograms()
      .then((r) => {
        if (!r.bError && r.oData) setAllPlanograms(r.oData);
        else toast("Failed to load planograms", "error");
      })
      .catch(() => toast("Failed to load planograms", "error"))
      .finally(() => {
        if (isNew) setLoading(false);
      });
  }, []);

  // Load existing planogram
  useEffect(() => {
    if (isNew) return;
    getPlanogram(paramId)
      .then((r) => {
        if (!r.bError && r.oData) {
          setCurrent(r.oData);
          setName(r.oData.Name);
          setDeleted(r.oData.Deleted);
        } else {
          toast("Failed to load planogram", "error");
        }
      })
      .catch(() => toast("Failed to load planogram", "error"))
      .finally(() => setLoading(false));
  }, [paramId, isNew]);

  // Filtered source lists (for New-mode "load from" picker)
  const basePlanograms = useMemo(() => {
    const bases = allPlanograms.filter((p) => p.IsBase);
    if (!search) return bases;
    const q = search.toLowerCase();
    return bases.filter((p) => (p.Name ?? "").toLowerCase().includes(q));
  }, [allPlanograms, search]);

  const normalPlanograms = useMemo(() => {
    const normals = allPlanograms.filter((p) => !p.IsBase);
    if (!search) return normals;
    const q = search.toLowerCase();
    return normals.filter((p) => (p.Name ?? "").toLowerCase().includes(q));
  }, [allPlanograms, search]);

  const slots = useMemo(() => {
    if (!current) return [];
    return current.ConfigDataSerialized.filter(
      (s) => showDisabled || !s.Disabled,
    );
  }, [current, showDisabled]);

  const totalSlots = current?.ConfigDataSerialized.length ?? 0;
  const disabledSlots = useMemo(
    () =>
      current?.ConfigDataSerialized.filter((s) => s.Disabled).length ?? 0,
    [current],
  );
  const rentableSlots = useMemo(
    () =>
      current?.ConfigDataSerialized.filter((s) => s.Rentable).length ?? 0,
    [current],
  );
  const suggestedSlots = useMemo(
    () => current?.ConfigDataSerialized.filter((s) => isHighlighted(s)).length ?? 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current, highlightedSlots],
  );

  const [selectedListId, setSelectedListId] = useState<number | null>(null);

  const performLoad = useCallback(
    (sourceId: number) => {
      const source = allPlanograms.find((p) => p.ID === sourceId);
      if (!source) return;
      setCurrent({ ...source, ID: -1, KioskID: -1 });
      setName("");
      toast(`Loaded from "${source.Name}".`, "success");
    },
    [allPlanograms, toast],
  );

  const requestLoad = useCallback(async (sourceId: number) => {
    const ok = await confirm({
      title: "Load planogram?",
      body: "Loading this planogram will replace any slots you've changed here. Continue?",
      confirmLabel: "Load",
    });
    if (ok) performLoad(sourceId);
  }, [confirm, performLoad]);

  const handleRequestLoad = () => {
    if (selectedListId == null) {
      toast("Select a planogram to load first.", "warning");
      return;
    }
    requestLoad(selectedListId);
  };

  const handleSlotSave = (data: Partial<PlanogramSlot>) => {
    if (!current) return;
    const updated = { ...current };
    if (editingSlot === "new") {
      const newSlot: PlanogramSlot = {
        SlotID: 0,
        ShelfID: 0,
        Sensor: 0,
        WeightType: 0,
        Rentable: data.Rentable ?? false,
        Disabled: data.Disabled ?? false,
        Name: "",
        ProductID: data.ProductID ?? 0,
        ProductCount: 0,
        ProductWeight: 0,
        MaxProductCount: data.MaxProductCount ?? 0,
        MinX: 0,
        MaxX: 0,
        MinY: 0,
        MaxY: 0,
      };
      updated.ConfigDataSerialized = [...updated.ConfigDataSerialized, newSlot];
    } else if (editingSlot) {
      updated.ConfigDataSerialized = updated.ConfigDataSerialized.map((s) =>
        s.SlotID === editingSlot.SlotID
          ? {
              ...s,
              ProductID: data.ProductID ?? s.ProductID,
              MaxProductCount: data.MaxProductCount ?? s.MaxProductCount,
              Rentable: data.Rentable ?? s.Rentable,
              Disabled: data.Disabled ?? s.Disabled,
            }
          : s,
      );
    }
    setCurrent(updated);
    setEditingSlot(null);
  };

  const handleSave = async () => {
    if (!current || !name.trim()) {
      toast("Provide a planogram name.", "warning");
      return;
    }
    if (isNew) {
      const duplicate = allPlanograms.some((p) => p.Name === name.trim());
      if (duplicate) {
        toast("Give the planogram a unique name.", "warning");
        return;
      }
    }
    setSaving(true);
    try {
      const plan: Planogram = {
        ...current,
        Name: name.trim(),
        Deleted: deleted,
        ID: isNew ? -1 : current.ID,
        KioskID: isNew ? -1 : current.KioskID,
        IsBase: false,
        DiffData: [],
      };
      const r = await savePlanogram(plan);
      if (r.bError) {
        toast(r.sMessage || "Save failed", "error");
      } else if (r.oData) {
        toast("Planogram saved.", "success");
        onSaved?.();
        onClose();
      } else {
        toast("Failed to save.", "error");
      }
    } catch {
      toast("Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const title = isNew ? "New planogram" : "Edit planogram";

  if (loading) {
    return (
      <Modal open={open} onClose={onClose} wide>
        <ModalHeader title={title} onClose={onClose} />
        <ModalBody>
          <div style={{ padding: "var(--space-4)" }}>Loading…</div>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} wide>
      <ModalHeader title={title} onClose={onClose} />
      <ModalBody>
        <div
          ref={statusRef}
          aria-live="polite"
          className={styles.srOnly}
        />

      <div className={styles.layout}>
        {/* ── Left: source picker (new mode only) ──────── */}
        {isNew ? (
          <aside
            className={styles.sidebar}
            aria-labelledby="source-picker-heading"
          >
            <div className={styles.panelHeader}>
              <h2 id="source-picker-heading" className={styles.panelTitle}>
                Start from
              </h2>
            </div>
            <p className={formStyles.hint}>
              Pick a base or existing planogram to copy slots from.
            </p>

            <label htmlFor="source-search" className={styles.srOnly}>
              Search planograms
            </label>
            <Input
              id="source-search"
              type="search"
              placeholder="Search planograms…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className={styles.panelHeader}>
              <h3 className={styles.panelTitle}>Base planograms</h3>
              <span className={styles.panelCount}>{basePlanograms.length}</span>
            </div>
            <div className={styles.listScrollSm}>
              <div className={styles.listScrollInner}>
                {basePlanograms.length === 0 ? (
                  <div className={styles.listEmpty}>No base planograms</div>
                ) : (
                  <ul
                    className={styles.list}
                    role="listbox"
                    aria-label="Base planograms"
                  >
                    {basePlanograms.map((p) => {
                      const isSel = selectedListId === p.ID;
                      return (
                        <li
                          key={p.ID}
                          role="option"
                          aria-selected={isSel}
                          className={isSel ? styles.listItemSelected : styles.listItem}
                          onClick={() => setSelectedListId(p.ID)}
                          onDoubleClick={() => requestLoad(p.ID)}
                        >
                          <span className={styles.listItemName} title={p.Name}>
                            {p.Name}
                          </span>
                          {p.KioskRevision && (
                            <span className={styles.listItemMeta}>
                              {p.KioskRevision}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className={styles.panelHeader}>
              <h3 className={styles.panelTitle}>Existing planograms</h3>
              <span className={styles.panelCount}>{normalPlanograms.length}</span>
            </div>
            <div className={styles.listScrollMd}>
              <div className={styles.listScrollInner}>
                {normalPlanograms.length === 0 ? (
                  <div className={styles.listEmpty}>No planograms</div>
                ) : (
                  <ul
                    className={styles.list}
                    role="listbox"
                    aria-label="Existing planograms"
                  >
                    {normalPlanograms.map((p) => {
                      const isSel = selectedListId === p.ID;
                      return (
                        <li
                          key={p.ID}
                          role="option"
                          aria-selected={isSel}
                          className={isSel ? styles.listItemSelected : styles.listItem}
                          onClick={() => setSelectedListId(p.ID)}
                          onDoubleClick={() => requestLoad(p.ID)}
                        >
                          <span className={styles.listItemName} title={p.Name}>
                            {p.Name}
                          </span>
                          {p.KioskRevision && (
                            <span className={styles.listItemMeta}>
                              {p.KioskRevision}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className={styles.loadRow}>
              <Button
                variant="secondary"
                onClick={handleRequestLoad}
                disabled={selectedListId == null}
              >
                Load selected
              </Button>
            </div>
          </aside>
        ) : (
          <div />
        )}

        {/* ── Right: current planogram ────────────────── */}
        <section
          className={styles.mainPanel}
          aria-labelledby="planogram-detail-heading"
        >
          <div className={styles.panelHeader}>
            <h2 id="planogram-detail-heading" className={styles.panelTitle}>
              Planogram details
            </h2>
          </div>

          <div className={formStyles.fieldRowStacked}>
            <label htmlFor="planogram-name">Planogram name</label>
            <Input
              id="planogram-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isNew}
              placeholder={isNew ? "Enter a unique name…" : ""}
            />
            {!isNew && (
              <span className={formStyles.hint}>
                Names are fixed once a planogram exists.
              </span>
            )}
          </div>

          {!current ? (
            <EmptyState
              title={isNew ? "No source loaded" : "No planogram data"}
              description={
                isNew
                  ? "Choose a planogram on the left and click Load selected (or double-click) to begin."
                  : "This planogram has no configuration data."
              }
            />
          ) : (
            <>
              <div className={styles.summaryBar} aria-label="Planogram summary">
                <div className={styles.summaryStat}>
                  <span className={styles.summaryLabel}>Total slots</span>
                  <span className={styles.summaryValue}>{totalSlots}</span>
                </div>
                <div className={styles.summaryStat}>
                  <span className={styles.summaryLabel}>Enabled</span>
                  <span className={styles.summaryValue}>
                    {totalSlots - disabledSlots}
                  </span>
                </div>
                <div className={styles.summaryStat}>
                  <span className={styles.summaryLabel}>Disabled</span>
                  <span className={styles.summaryValue}>{disabledSlots}</span>
                </div>
                <div className={styles.summaryStat}>
                  <span className={styles.summaryLabel}>Rentable</span>
                  <span className={styles.summaryValue}>{rentableSlots}</span>
                </div>
                {suggestedSlots > 0 && (
                  <div className={styles.summaryStat}>
                    <span className={styles.summaryLabel}>Suggested</span>
                    <span className={styles.summaryValue}>{suggestedSlots}</span>
                  </div>
                )}
                <div className={styles.summarySpacer} />
                <Checkbox
                  label="Show disabled slots"
                  checked={showDisabled}
                  onChange={(e) => setShowDisabled(e.target.checked)}
                />
                {isNew && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditingSlot("new")}
                  >
                    Add slot
                  </Button>
                )}
              </div>

              <div className={styles.tableWrap}>
                <table
                  className={styles.slotTable}
                  aria-label="Planogram slots"
                >
                  <thead>
                    <tr>
                      {isNew && (
                        <th scope="col" style={{ width: "var(--space-10)" }}>
                          <span className={styles.srOnly}>Edit</span>
                        </th>
                      )}
                      <th scope="col">Name</th>
                      <th scope="col">Shelf</th>
                      <th scope="col">Slot</th>
                      <th scope="col">Type</th>
                      <th scope="col">Product</th>
                      <th scope="col" className={styles.slotTableNumeric}>
                        Max
                      </th>
                      <th scope="col" className={styles.slotTableNumeric}>
                        Sensor
                      </th>
                      <th scope="col">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.length === 0 ? (
                      <tr>
                        <td
                          colSpan={isNew ? 9 : 8}
                          className={formStyles.emptyRow}
                        >
                          {showDisabled
                            ? "No slots configured"
                            : "No enabled slots — toggle Show disabled slots to see all"}
                        </td>
                      </tr>
                    ) : (
                      slots.map((s, i) => (
                        <tr
                          key={`${s.ShelfID}-${s.SlotID}-${i}`}
                          className={isHighlighted(s) ? styles.slotRowChanged : undefined}
                        >
                          {isNew && (
                            <td>
                              <button
                                type="button"
                                className={styles.slotEditBtn}
                                aria-label={`Edit slot ${s.Name || `${s.ShelfID}·${s.SlotID}`}`}
                                onClick={() => setEditingSlot(s)}
                              >
                                <StatusIcon kind="edit" size={16} />
                              </button>
                            </td>
                          )}
                          <td>{s.Name}</td>
                          <td>{s.ShelfID}</td>
                          <td>{s.SlotID}</td>
                          <td>
                            <Badge variant={s.Rentable ? "primary" : "default"}>
                              {s.Rentable ? "Rent" : "Buy"}
                            </Badge>
                          </td>
                          <td className={styles.slotProduct}>
                            {s.ProductID > 0 ? (
                              productMap.get(s.ProductID) ?? `ID: ${s.ProductID}`
                            ) : (
                              <span className={styles.slotProductNone}>—</span>
                            )}
                            {isHighlighted(s) && (
                              <>
                                {" "}
                                <Badge variant="primary">Suggested</Badge>
                              </>
                            )}
                          </td>
                          <td className={styles.slotTableNumeric}>
                            {s.MaxProductCount}
                          </td>
                          <td className={styles.slotTableNumeric}>{s.Sensor}</td>
                          <td>
                            <Badge variant={s.Disabled ? "warning" : "success"}>
                              {s.Disabled ? "Disabled" : "Enabled"}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>

      </ModalBody>
      <ModalFooter>
        {!isNew && (
          <Button variant="secondary" onClick={() => setPushOpen(true)}>
            Push to kiosk…
          </Button>
        )}
        <Checkbox
          label="Mark deleted"
          checked={deleted}
          onChange={(e) => setDeleted(e.target.checked)}
        />
        <div style={{ flex: 1 }} />
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={saving}
          disabled={!current}
        >
          {isNew ? "Create planogram" : "Save changes"}
        </Button>
      </ModalFooter>

      {/* Slot edit modal */}
      <SlotEditModal
        open={editingSlot !== null}
        slot={editingSlot === "new" ? null : editingSlot}
        isNew={editingSlot === "new"}
        products={products}
        onSave={handleSlotSave}
        onClose={() => setEditingSlot(null)}
      />

      {/* Push modal */}
      <PushPlanogramDialog
        open={pushOpen && !!current}
        planogramId={current?.ID ?? 0}
        kiosks={kiosks}
        onClose={() => setPushOpen(false)}
      />

    </Modal>
  );
}

/* ── Route compat wrapper ──────────────────────────────── */
/* Redirects /page/editplanogram?id=X to /page/viewplanograms?edit=X so the
   grid page can own the dialog state. Keeps legacy hyperlinks working. */
export function EditPlanogramPage() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? "-1";
  return (
    <Navigate
      to={`${getPagePath("viewplanograms")}?edit=${encodeURIComponent(id)}`}
      replace
    />
  );
}
